import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Modal } from "antd";
import { Button, Form, Row } from "reactstrap";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateForgetPassword } from '../services/authApi';
import ImageLogin from "../../../assets/images/loginImg.png";
import logo from "../../../assets/images/Paxity.png";
import '../utils/login.css';
import FormField from '../../../common/Form/FormField';
import { toast } from "react-toastify";
import { EyeInvisibleTwoTone, MailOutlined } from '@ant-design/icons';
import Lottie from "lottie-react";
import securityAnim from "../../../assets/animations/security.json";
import { EyeTwoTone } from "@ant-design/icons";

const UpdateForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(8, "Le mot de passe doit contenir au moins 8 caractères")
      .max(64, "Le mot de passe ne doit pas dépasser 64 caractères")
      .matches(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
      .matches(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
      .matches(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre")
      .matches(/[^A-Za-z0-9]/, "Le mot de passe doit contenir au moins un caractère spécial")
      .matches(/^\S*$/, "Le mot de passe ne doit pas contenir d'espaces")
      .required("Le nouveau mot de passe est obligatoire"),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword")], "Les mots de passe doivent être identiques")
      .required("Merci de confirmer le nouveau mot de passe"),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: ""
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!token) {
        toast.error("Lien invalide ou expiré.");
        setError("Lien de réinitialisation invalide ou expiré.");
        return;
      }

      setIsLoading(true);
      setError("");
      const data = {
        token: token,
        newPassword: values.newPassword
      };

      try {
        await updateForgetPassword(data);
        setIsModalVisible(true);
      } catch (error) {
        console.log('error', error)
        console.log('error?.message', error?.message)
        console.log('error?.error', error?.error)
        setError(error?.message || "Une erreur est survenue");
        toast.error(`Erreur : ${error}`);
      } finally {
        setIsLoading(false);
      }
    }
  });

  const handleOk = () => {
    formik.resetForm();
    navigate("/login-merchant");
    setIsModalVisible(false);
  };

  return (
    <div className="my-box d-flex">
      <div className="left-box">
        <img src={ImageLogin} alt="Logo" className="login-image" />
      </div>
      <div className="right-box mt-10">
        <div className="card-body py-2 px-md-5">
          <img src={logo} alt="Logo" className='mt-2 mb-4' />

          <div className="auth-heading-wrapper">
            <div className=" connected">Modification de mot de passe !</div>
            <p className="texteLogin mt-1 mb-0">
              Veuillez saisir votre nouveau mot de passe puis le confirmer pour finaliser la modification.
            </p>
          </div>

          {error && <div className="alert alert-danger">{error || "Erreur d'update, contactez le support"}</div>}

          <Form onSubmit={formik.handleSubmit} disabled={!token}>
            <Row className="mt-6">
              <FormField
                label="Nouveau mot de passe"
                name="newPassword"
                formik={formik}
                type={showPassword ? "text" : "password"}
                colSize={12}
                icon={
                  showPassword ? (
                    <EyeInvisibleTwoTone twoToneColor="#ff4d4f"
                      onClick={() => setShowPassword(false)}
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    <EyeTwoTone
                      onClick={() => setShowPassword(true)}
                      style={{ cursor: "pointer" }}
                    />
                  )
                }
              />

              <FormField
                label="Confirmer le mot de passe"
                name="confirmPassword"
                formik={formik}
                type={showConfirm ? "text" : "password"}
                colSize={12}
                icon={
                  showConfirm ? (
                    <EyeInvisibleTwoTone twoToneColor="#ff4d4f" 
                      onClick={() => setShowConfirm(false)}
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    <EyeTwoTone
                      onClick={() => setShowConfirm(true)}
                      style={{ cursor: "pointer" }}
                    />
                  )
                }
              />

            </Row>
            <Button
              disabled={isLoading || !(formik.isValid && formik.dirty)}
              type="submit"
              style={{ backgroundColor: "#2848FF" }}
              className="login-btn mt-4"
            >
              {isLoading ? <Spin size="small" /> : "Modifier"}
            </Button>
          </Form>

          <div className="text-center mt-4">
            Ah je me rappelle de mon compte !{" "}
            <a href="/login-merchant" style={{ color: "#2848FF" }}>
              Se connecter
            </a>
          </div>
        </div>
      </div>

      <Modal
        title={null}
        open={isModalVisible}
        onCancel={handleOk}
        footer={[
          <div key="footer" style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Button
              type="primary"
              style={{
                backgroundColor: "#2848FF",
                borderRadius: "8px",
                width: "60%",
              }}
              onClick={handleOk}
            >
              Fermer
            </Button>
          </div>
        ]}
        centered
        width={500}
        bodyStyle={{
          padding: "40px 30px",
          textAlign: "center",
          borderRadius: "12px",
        }}
      >
        <div style={{ width: 200, margin: "0 auto", marginBottom: 20 }}>
          <Lottie animationData={securityAnim} loop={true} autoplay />
        </div>

        <h2 style={{ fontWeight: 500 }}>
          Mot de passe mis à jour avec sucès <MailOutlined style={{ color: "#2848FF" }} />
        </h2>
        <p style={{ fontSize: "16px", color: "#555", marginBottom: 0 }}>
          Vous pouvez désormais vous connecter avec votre nouveau mot de passe.
        </p>
      </Modal>
    </div>
  );
};

export default UpdateForgotPassword;
